import React from 'react';
import PropTypes from 'prop-types';
import { Card, ContentCard, FilterSetController, Table, Tooltip, Icon, Numbers, Chip, MiniTab, CheckboxLabel, TooltipHelp } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../context/useAppContext';
import Style from './channelsStats.module.css';
import i18n from 'i18next';
import { DISTRIBUTOR_ID_MALCOLM, DISTRIBUTOR_ID_PARTNERS } from '../../models/distributor';
import { capitalizeFirstLetter } from '../attendee/detail/components/utils';
import LoadingTable from '../../components/loadingTable';

const EMPTY_STATS_CHANNEL = {
    count_attendees_active: 0,
    count_attendees_deleted: 0,
    count_attendees_reservation: 0,
    sum_revenue_active: 0,
    sum_commission_active: 0,
    sum_revenue_deleted: 0,
    sum_commission_deleted: 0,
    sum_revenue_reservation: 0,
    sum_commission_reservation: 0
};

export function LabelStat({ label = null, stat = 0, locale = 'en-GB', currency = null }) {
    return (
        <span>
            <span className={Style['margin-right-x-small']}>+</span>
            <span className={Style['margin-right-x-small']}>
                <Numbers locale={locale} value={stat} currency={currency} showNullDecimal={Boolean(currency)} />
            </span>
            {label && <span>{label}</span>}
        </span>
    );
}

export const CountAttendees = React.memo(({ data = {} }) => {
    const locale = i18n.language;
    const { t } = useTranslation();

    return (
        <div className={Style['td_stat_container']}>
            <span className={Style['margin-right-small']}>
                <Numbers locale={locale} value={data?.count_attendees_active || 0} />
            </span>
            {data?.count_attendees_deleted > 0 && (
                <span className={Style['margin-right-x-small']}>
                    <Chip
                        text={
                            <LabelStat
                                label={t('ticket.attendees_stats.channels.labelCountDeleted', { count: data.count_attendees_deleted })}
                                stat={data.count_attendees_deleted}
                                locale={locale}
                            />
                        }
                    />
                </span>
            )}
            {data?.count_attendees_reservation > 0 && (
                <Tooltip
                    triggerOnHover
                    title={
                        <LabelStat
                            label={t('ticket.attendees_stats.channels.labelCountReservation', { count: data.count_attendees_reservation })}
                            stat={data.count_attendees_reservation}
                            locale={locale}
                        />
                    }
                >
                    <Icon name={'time'} />
                </Tooltip>
            )}
        </div>
    );
});

const SumRevenuesChannel = React.memo(({ data = {}, isNetReceipts = false }) => {
    const locale = i18n.language;
    const { t } = useTranslation();
    const [{ event }] = useAppContext();

    const sumRevenueActive = React.useMemo(() => {
        return isNetReceipts ? data.sum_revenue_active - data.sum_commission_active : data.sum_revenue_active;
    }, [data, isNetReceipts]);

    const sumRevenueDeleted = React.useMemo(() => {
        return isNetReceipts ? data.sum_revenue_deleted - data.sum_commission_deleted : data.sum_revenue_deleted;
    }, [data, isNetReceipts]);

    const sumRevenueReservation = React.useMemo(() => {
        return isNetReceipts ? data.sum_revenue_reservation - data.sum_commission_reservation : data.sum_revenue_reservation;
    }, [data, isNetReceipts]);

    return (
        <div className={Style['td_stat_container']}>
            <span className={Style['margin-right-small']}>
                <Numbers locale={locale} value={sumRevenueActive} currency={event.currency || 'EUR'} showNullDecimal />
            </span>
            {sumRevenueDeleted > 0 && (
                <span className={Style['margin-right-x-small']}>
                    <Chip
                        text={
                            <LabelStat label={t('ticket.attendees_stats.channels.labelSumDeleted')} stat={sumRevenueDeleted} locale={locale} currency={event.currency || 'EUR'} />
                        }
                    />
                </span>
            )}
            {sumRevenueReservation > 0 && (
                <Tooltip
                    triggerOnHover
                    title={
                        <LabelStat
                            label={t('ticket.attendees_stats.channels.labelSumReservation')}
                            stat={sumRevenueReservation}
                            locale={locale}
                            currency={event.currency || 'EUR'}
                        />
                    }
                >
                    <Icon name={'time'} />
                </Tooltip>
            )}
        </div>
    );
});

function HeaderRow() {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader>
            <th className={`${Style['width-x-large']} ${Style['cell_name']}`}>{t('ticket.common.channel_plural')}</th>
            <th className={Style['width-x-large']}>{t('ticket.common.attendee_plural')}</th>
            <th className={Style['width-x-large']}>{t('ticket.common.receipt_plural')}</th>
        </Table.Tr>
    );
}

function LabelName({ data }) {
    const { t } = useTranslation();

    return (
        <span>
            {capitalizeFirstLetter(data.label)}
            {data.id === 'unknown' && <TooltipHelp position="bottom" triggerOnHover title={t('ticket.attendees_stats.channels.unknownTooltip')} />}
        </span>
    );
}

export const ChannelStatRow = React.memo(({ name, data, isNetReceipts }) => {
    return (
        <Table.Tr>
            <Table.Td className={`${Style['width-x-large']} ${Style['cell_name']}`}>
                <LabelName data={data} />
            </Table.Td>
            <Table.Td className={Style['width-x-large']}>
                <CountAttendees data={data} />
            </Table.Td>
            <Table.Td className={Style['width-x-large']}>
                <SumRevenuesChannel data={data} isNetReceipts={isNetReceipts} />
            </Table.Td>
        </Table.Tr>
    );
});

export const TableContent = React.memo(({ stats = null, countChannels = 0, isNetReceipts = false }) => {
    return (
        <Table.TBody>
            {Boolean(stats && countChannels) &&
                Object.entries(stats).map(([key, value]) => {
                    return value.map(stat => <ChannelStatRow key={stat.id} name={stat.label || stat.id} data={stat} isNetReceipts={isNetReceipts} />);
                })}
        </Table.TBody>
    );
});

export function QuickFilters({ isWoSalesChannels = false, onChangeIsWoSalesChannels = () => {} }) {
    const { t } = useTranslation();

    return (
        <div className={Style['filter-checkboxes']}>
            <span className={Style['filter-checkboxes-label']}>{`${t('ticket.common.action.display')}: `}</span>
            <CheckboxLabel
                label={t('ticket.attendees_stats.channels.externalChannelsWoSales')}
                checked={isWoSalesChannels}
                onChange={(_, data) => {
                    onChangeIsWoSalesChannels(data.checked);
                }}
            />
        </div>
    );
}

export default function ChannelsStats({ stats = null, distributors = [], loadingDistributors = false }) {
    const { t } = useTranslation();

    const [search, setSearch] = React.useState(null);
    const [isNetReceipts, setIsNetReceipts] = React.useState(false);
    const [isWoSalesChannels, setIsWoSalesChannels] = React.useState(false);

    const statistics = React.useMemo(() => {
        if (!stats) {
            return null;
        }

        let statsFinded = { ...stats.find(groupStats => groupStats.name === 'sales_by_channel_stats').value };

        //We are reorganizing statistics data so that it is more easily filterable and displayable.
        // Expected result
        // _statistics = {
        //   main_channels: [array main_channels_stats]
        //   distributors: [array distributors_stats]
        //}
        let _statistics = {};
        let statsMainChannels = [];

        //We add here a label translated on the main channels so that it can be used by the search in
        //the useMemo filteredStatistics
        Object.entries(statsFinded).forEach(([key, value]) => {
            if (!['total', 'distributors'].includes(key)) {
                statsMainChannels.push({ ...value, label: t(`ticket.attendees_stats.channels.${key}`) });
            }
        });
        _statistics['main_channels'] = statsMainChannels;

        let statisticsDistributors = { ...statsFinded['distributors'] };
        _statistics['distributors'] = Object.values(statisticsDistributors);

        //In the statistics, we receive only distributor channels with sales.
        //As soon as the list of distributors is accessible, we reorganize the distributor channels,
        //so that we can also display the distributor channels without sales
        if (!loadingDistributors && distributors) {
            //We exclude here default distributors malcolm and hogwarts because these sales are already
            //counted in the invitations sales channel
            let defaultDistributors = distributors.filter(d => !d.organization && ![DISTRIBUTOR_ID_MALCOLM, DISTRIBUTOR_ID_PARTNERS].includes(d.id));
            //We start with the distributors by default, by retrieving the data in the statistics
            //if they exist otherwise we populate with empty statistics
            let _statsDefaultDistributors = defaultDistributors.reduce((acc, { id, label }) => {
                acc.push(statisticsDistributors[id] || { ...EMPTY_STATS_CHANNEL, id, label });
                return acc;
            }, []);

            //Here we are dealing with the particular case of a 'distributor origin' but
            //with a 'distributor_id' === null.(due to sync issue)
            //They will be displayed in front as "external ticketing"
            if (statisticsDistributors['unknown']) {
                _statsDefaultDistributors.push({ ...statisticsDistributors['unknown'], label: t('ticket.attendees_stats.channels.unknown') });
            }

            //Finally, here identically to the default distributors, we treat the distributors linked to the organization
            let organizationDistributors = distributors.filter(d => d.organization);
            let _statsOrgaDistributors = organizationDistributors.reduce((acc, { id, label }) => {
                acc.push(statisticsDistributors[id] || { ...EMPTY_STATS_CHANNEL, id, label });
                return acc;
            }, []);

            statisticsDistributors = [..._statsDefaultDistributors, ..._statsOrgaDistributors];

            _statistics['distributors'] = statisticsDistributors;
        }

        return _statistics;
    }, [stats, distributors, loadingDistributors]);

    const filterStatsChannels = (value, search) => {
        if (search) {
            const reg = new RegExp(search, 'gi');
            return reg.test(value);
        }

        return true;
    };

    const filterStatsDistributors = (isWoSalesChannels, statWithSale, value, search) => {
        if (!isWoSalesChannels && !statWithSale) {
            return false;
        }
        return filterStatsChannels(value, search);
    };

    //This useMemo is used to filter the channels, it is triggered by the seach and the quick filter
    const filteredStatistics = React.useMemo(() => {
        if (!statistics) {
            return null;
        }

        let _statistics = { ...statistics };

        if (search) {
            let filteredStatsMainChannels = statistics['main_channels'].filter(s => filterStatsChannels(s.label, search));
            _statistics['main_channels'] = [...filteredStatsMainChannels];
        }

        let filteredStatsDistributors = statistics['distributors'].filter(s => filterStatsDistributors(isWoSalesChannels, s.count_attendees_active > 0, s.label, search));
        _statistics['distributors'] = [...filteredStatsDistributors];

        return _statistics;
    }, [statistics, isWoSalesChannels, search]);

    const countChannels = React.useMemo(() => {
        if (!filteredStatistics) {
            return 0;
        }

        return filteredStatistics['main_channels'].length + filteredStatistics['distributors'].length;
    }, [filteredStatistics]);

    return (
        <div>
            <Card separator="false">
                <ContentCard>
                    <FilterSetController
                        total={!filteredStatistics ? '--' : countChannels}
                        labelTotal={t('ticket.common.channel', { count: countChannels })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            onChange: (synth, value) => {
                                setSearch(value);
                            },
                            handleReset: () => {
                                setSearch(null);
                            },
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: search || ''
                        }}
                        onChange={() => {}}
                    />
                    <div className={Style['quickactions_container']}>
                        <QuickFilters isWoSalesChannels={isWoSalesChannels} onChangeIsWoSalesChannels={setIsWoSalesChannels} />
                        <div className={Style['minitab_receipts_container']}>
                            <span className={Style['minitab_receipts_label']}>{t('ticket.attendees_stats.channels.quickActions.receiptsLabel')}</span>
                            <MiniTab
                                panes={[
                                    {
                                        label: t('ticket.attendees_stats.channels.quickActions.grossReceipts'),
                                        value: false
                                    },
                                    {
                                        label: t('ticket.attendees_stats.channels.quickActions.netReceipts'),
                                        value: true
                                    }
                                ]}
                                activeTab={isNetReceipts}
                                onTabSelected={tab => {
                                    setIsNetReceipts(tab.value);
                                }}
                            />
                        </div>
                    </div>
                </ContentCard>
                <Table useNew>
                    <Table.THead>
                        <HeaderRow />
                    </Table.THead>
                    <TableContent stats={filteredStatistics} countChannels={countChannels} isNetReceipts={isNetReceipts} />
                </Table>
                <LoadingTable loading={!stats} />
                {stats && countChannels === 0 && <Card.EmptyState title={t('ticket.attendees_stats.channels.noChannelFound')} icon={<Icon name="cart" />} />}
            </Card>
        </div>
    );
}

ChannelsStats.propTypes = {
    stats: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    distributors: PropTypes.array,
    loadingDistributors: PropTypes.bool
};
